import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import AdminLink from '../components/AdminLink';
import { apiFetch } from '../jscontrol/apiClient';
import {
  formatCartDate,
  formatCartMoney,
  filterCartList,
  paginateList,
  parseCartListResponse,
  sortCartList,
} from './cartUtils';
import '../css/Category.css';

const CartPage = () => {
  const navigate = useNavigate();
  const [allCarts, setAllCarts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [filterText, setFilterText] = useState('');
  const [sortKey, setSortKey] = useState('CreatedAt');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [PAGE_SIZE, setPageSize] = useState(10);

  const fetchCarts = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiFetch('/Cart/Admin/GetAll');
      setAllCarts(parseCartListResponse(data));
    } catch (e) {
      setError(e.message);
      setAllCarts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCarts();
  }, []);

  const filteredCarts = useMemo(
    () => sortCartList(filterCartList(allCarts, filterText), sortKey, sortDir),
    [allCarts, filterText, sortKey, sortDir]
  );

  const totalCount = filteredCarts.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE) || 1);
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const carts = useMemo(
    () => paginateList(filteredCarts, safePage, PAGE_SIZE),
    [filteredCarts, safePage, PAGE_SIZE]
  );

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const goToPage = (p) => setPage(Math.max(1, Math.min(p, totalPages)));
  const handleFilter = (val) => { setFilterText(val); setPage(1); };
  const handlePageSize = (val) => { setPageSize(Number(val)); setPage(1); };

  const handleSortCol = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <i className="mdi mdi-sort ms-1" style={{ opacity: 0.3 }}></i>;
    return sortDir === 'asc'
      ? <i className="mdi mdi-sort-ascending ms-1" style={{ color: '#6f42c1' }}></i>
      : <i className="mdi mdi-sort-descending ms-1" style={{ color: '#6f42c1' }}></i>;
  };

  const renderPages = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(2, safePage - delta);
    const right = Math.min(totalPages - 1, safePage + delta);

    pages.push(
      <button key={1} type="button" className={`cat-page-btn${safePage === 1 ? ' active' : ''}`} onClick={() => goToPage(1)}>1</button>
    );
    if (left > 2) pages.push(<span key="l-ellipsis" className="cat-page-ellipsis">…</span>);
    for (let i = left; i <= right; i++) {
      if (i === 1 || i === totalPages) continue;
      pages.push(
        <button key={i} type="button" className={`cat-page-btn${safePage === i ? ' active' : ''}`} onClick={() => goToPage(i)}>{i}</button>
      );
    }
    if (right < totalPages - 1) pages.push(<span key="r-ellipsis" className="cat-page-ellipsis">…</span>);
    if (totalPages > 1) {
      pages.push(
        <button key={totalPages} type="button" className={`cat-page-btn${safePage === totalPages ? ' active' : ''}`} onClick={() => goToPage(totalPages)}>{totalPages}</button>
      );
    }
    return pages;
  };

  const showingFrom = totalCount === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const showingTo = totalCount === 0 ? 0 : Math.min(safePage * PAGE_SIZE, totalCount);

  return (
    <AdminLayout>
      <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6c757d' }}>
        <AdminLink to="/admin">Dashboard</AdminLink>
        <span style={{ margin: '0 6px' }}>/</span>
        <span>Carts</span>
      </p>

      <div className="row">
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h4 className="card-title mb-0">Carts</h4>
                <div className="cat-filter-wrap">
                  <i className="mdi mdi-magnify cat-filter-icon"></i>
                  <input
                    type="text"
                    className="cat-filter-input"
                    placeholder="Search cart, user, status..."
                    value={filterText}
                    onChange={(e) => handleFilter(e.target.value)}
                  />
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : error ? (
                <div className="alert alert-danger d-flex align-items-center py-2" style={{ fontSize: '0.875rem' }}>
                  <i className="mdi mdi-alert-circle me-2" style={{ fontSize: '1.1rem' }}></i>
                  {error}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped mb-0">
                    <thead>
                      <tr>
                        <th className="cat-sort-th" onClick={() => handleSortCol('CartId')} style={{ width: '10%' }}>
                          Cart ID <SortIcon col="CartId" />
                        </th>
                        <th className="cat-sort-th" onClick={() => handleSortCol('Username')} style={{ width: '18%' }}>
                          Username <SortIcon col="Username" />
                        </th>
                        <th className="cat-sort-th" onClick={() => handleSortCol('CartStatus')} style={{ width: '14%' }}>
                          Status <SortIcon col="CartStatus" />
                        </th>
                        <th className="cat-sort-th" onClick={() => handleSortCol('CreatedAt')} style={{ width: '20%' }}>
                          Created At <SortIcon col="CreatedAt" />
                        </th>
                        <th className="cat-sort-th" onClick={() => handleSortCol('TotalQuantity')} style={{ width: '12%' }}>
                          Qty <SortIcon col="TotalQuantity" />
                        </th>
                        <th className="cat-sort-th" onClick={() => handleSortCol('GrandTotal')} style={{ width: '14%' }}>
                          Grand Total <SortIcon col="GrandTotal" />
                        </th>
                        <th style={{ width: '12%' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {carts.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center py-4 text-muted">No carts found.</td>
                        </tr>
                      ) : (
                        carts.map((cart) => (
                          <tr key={cart.cartId}>
                            <td>{cart.cartId}</td>
                            <td>{cart.userName}</td>
                            <td>
                              <span className="badge badge-info">{cart.cartStatus}</span>
                            </td>
                            <td>{formatCartDate(cart.createdAt)}</td>
                            <td>{cart.totalQuantity}</td>
                            <td>{formatCartMoney(cart.totalAmount)}</td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => navigate(`/admin/cart/view/${cart.cartId}`)}
                              >
                                <i className="mdi mdi-eye me-1"></i> View
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {!loading && !error && (
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <span className="cat-page-info">Rows per page:</span>
                    <select className="cat-pagesize-select" value={PAGE_SIZE} onChange={(e) => handlePageSize(e.target.value)}>
                      <option value={10}>10</option>
                      <option value={30}>30</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="cat-page-info">
                      Showing {showingFrom}–{showingTo} of {totalCount}
                    </span>
                  </div>
                  <div className="cat-pagination">
                    <button type="button" className="cat-page-btn" onClick={() => goToPage(1)} disabled={safePage === 1 || totalCount === 0} title="First">
                      <i className="mdi mdi-chevron-double-left"></i>
                    </button>
                    <button type="button" className="cat-page-btn" onClick={() => goToPage(safePage - 1)} disabled={safePage === 1 || totalCount === 0} title="Previous">
                      <i className="mdi mdi-chevron-left"></i>
                    </button>
                    {totalCount > 0 && renderPages()}
                    <button type="button" className="cat-page-btn" onClick={() => goToPage(safePage + 1)} disabled={safePage === totalPages || totalCount === 0} title="Next">
                      <i className="mdi mdi-chevron-right"></i>
                    </button>
                    <button type="button" className="cat-page-btn" onClick={() => goToPage(totalPages)} disabled={safePage === totalPages || totalCount === 0} title="Last">
                      <i className="mdi mdi-chevron-double-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CartPage;
